import React, { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PageTitle, PageContent, ConfirmModal } from 'nallian-shared-ui/lib/components';
import { Table, Params, Actions, Menu } from 'nallian-table-builder-ui';
import { INFINITE_SCROLL, IDENTITIES_RESOURCE } from '../constants';
import apiRoutes from '../../../app/api/routes';
import PopupAndCopy from '../../app/components/PopupAndCopy';
import PrincipalCountPopup from './PrincipalCountPopup';
import TextFilter from '../../app/components/TextFilter';
import AnonymizeIdentityService from '../services/anonymizeIdentityService';
import TableValue from '../../communities/components/relations/table/TableValue';
import IdentityStatus from './IdentityStatus';
import ActiveFilter from './ActiveFilter';

const convertFilters = ({ isActive, ...filters }) => ({
  ...filters,
  isActive: isActive === 1 ? true : isActive === 0 ? false : isActive,
});

const IdentitiesOverview = ({ dispatchAnonymizeIdentityPost, anonymizeIdentityFetch }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [anonymizeItem, setAnonymizeItem] = useState(null);

  const idParam = searchParams.get('id');

  const filtered = useMemo(() => {
    if (idParam) {
      return {
        defaults: { id: idParam },
        convertor: convertFilters,
      };
    }
    return {
      defaults: {},
      convertor: convertFilters,
    };
  }, [idParam]);

  useEffect(() => {
    if (idParam) {
      // Remove the query parameter after applying the filter
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [idParam, searchParams, setSearchParams]);

  const handleSetAnonymizeItem = useCallback((_, item) => setAnonymizeItem(item), []);

  const handleAnonymize = useCallback(() => {
    if (anonymizeItem?.id) {
      dispatchAnonymizeIdentityPost(
        anonymizeItem.id,
        anonymizeItem,
        () => setAnonymizeItem(null),
        t
      );
    }
  }, [anonymizeItem, dispatchAnonymizeIdentityPost, t]);

  return (
    <>
      <PageTitle>{t('allIdentities')}</PageTitle>
      <PageContent>
        <Table
          infiniteScroll={INFINITE_SCROLL}
          resource={IDENTITIES_RESOURCE}
          url={apiRoutes.identitiesOverview()}
          noResultsMessage={t('noIdentitiesFound')}
          pageSize={50}
          rowHeight={52}
          filtered={filtered}
          sorted
          noUserSettings
          resizable
        >
          <Params.Custom
            content={TableValue}
            placeholder={t('typeMinimum2Characters')}
            name="fullName"
            label="fullName"
            width="16rem"
            minWidth="16rem"
            filter={TextFilter}
            noFilterLabel
            grow
          />
          <Params.Custom
            content={PopupAndCopy}
            filter={TextFilter}
            placeholder={t('typeMinimum2Characters')}
            name="emailAddress"
            filterName="email"
            label="email"
            width="23rem"
            minWidth="23rem"
            noSort
            noFilterLabel
            grow
          />
          <Params.Custom
            content={PopupAndCopy}
            filter={TextFilter}
            placeholder={t('startWithPlusEgFormat')}
            name="phoneNumber"
            width="12rem"
            minWidth="12rem"
            noFilterLabel
            noSort
            grow
          />
          <Params.Custom
            content={PrincipalCountPopup}
            name="principalCount"
            label="linkedPrincipals"
            width="9rem"
            minWidth="9rem"
            grow
            noSort
            noFilter
          />
          <Params.Custom
            content={PopupAndCopy}
            filter={TextFilter}
            name="id"
            width="22rem"
            minWidth="22rem"
            noSort
            noFilterLabel
            grow
          />
          <Params.String
            name="identityProvider.name"
            label="provider"
            width="12rem"
            minWidth="12rem"
            noFilter
            grow
          />
          <Params.Custom
            content={IdentityStatus}
            filter={ActiveFilter}
            name="isActive"
            label="status"
            width="8rem"
            minWidth="8rem"
            noSort
            grow
          />
          <Menu width="3.5rem" align="center">
            <Actions.Button
              icon="user delete"
              label={t('anonymizeIdentity')}
              content={t('anonymizeIdentity')}
              onClick={handleSetAnonymizeItem}
            />
          </Menu>
        </Table>
        {anonymizeItem && (
          <ConfirmModal
            header={t('areYouSureYouWantToAnonymiseIdentity', {
              fullName: anonymizeItem.fullName || '-',
            })}
            cancelContent={t('cancel')}
            confirmContent={t('anonymizeIdentity')}
            confirmColor="red"
            onConfirm={handleAnonymize}
            onCancel={() => setAnonymizeItem(null)}
            open
          />
        )}
      </PageContent>
    </>
  );
};

IdentitiesOverview.propTypes = {
  dispatchAnonymizeIdentityPost: PropTypes.func.isRequired,
  anonymizeIdentityFetch: PropTypes.object.isRequired,
};

export default AnonymizeIdentityService(IdentitiesOverview);
